import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export interface RouletteSector {
  id: number;
  /** Colour family for the wedge — matches the ore it pays, or `aether` for the void. */
  accent: 'titan' | 'silicate' | 'aether';
}

export interface RouletteWheelProps {
  sectors: RouletteSector[];
  /** The landed sector — known before the spin animation starts. */
  result: number | null;
  /** Flips true to start a new spin. */
  spinning: boolean;
  onSettled?: () => void;
  size?: number;
}

const ACCENT_VAR: Record<RouletteSector['accent'], string> = {
  titan: '--sg-titan',
  silicate: '--sg-silicate',
  aether: '--sg-aether',
};

/**
 * A real spinning wheel, not a grid of buttons that flash a colour.
 *
 * The wheel never resets to 0° between spins — rotation only ever increases —
 * so consecutive spins always feel like forward motion instead of snapping
 * back to a start position. `result` is already the true outcome; the wheel's
 * job is purely to land the pointer on it convincingly.
 */
export function RouletteWheel({ sectors, result, spinning, onSettled, size = 240 }: RouletteWheelProps) {
  const [rotation, setRotation] = useState(0);
  const [justSettled, setJustSettled] = useState(false);
  const rotationRef = useRef(0);
  const spinToken = useRef(0);

  const anglePerSector = 360 / sectors.length;

  const gradient = sectors
    .map((sector, i) => {
      const from = i * anglePerSector;
      const to = from + anglePerSector;
      return `rgb(var(${ACCENT_VAR[sector.accent]})) ${from}deg ${to}deg`;
    })
    .join(', ');

  useEffect(() => {
    if (!spinning || result === null) return;
    const token = ++spinToken.current;
    setJustSettled(false);

    // Sector i's centre sits at i * anglePerSector clockwise from the top. To
    // bring it under the fixed top pointer the wheel must rest at
    // (360 - centre) mod 360 — then keep adding full turns plus whatever
    // forward delta is needed so the wheel never rotates backward.
    const restAngle = (360 - result * anglePerSector - anglePerSector / 2 + 360) % 360;
    const currentMod = ((rotationRef.current % 360) + 360) % 360;
    let delta = restAngle - currentMod;
    if (delta <= 0) delta += 360;
    const target = rotationRef.current + delta + 360 * 5; // five extra full spins for effect

    rotationRef.current = target;
    setRotation(target);

    const timer = window.setTimeout(() => {
      if (spinToken.current !== token) return;
      setJustSettled(true);
      onSettled?.();
    }, 3300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, result]);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Pointer — fixed, the wheel moves under it. */}
      <div
        aria-hidden
        className="absolute left-1/2 top-0 z-20 h-0 w-0 -translate-x-1/2 -translate-y-1"
        style={{
          borderLeft: '9px solid transparent',
          borderRight: '9px solid transparent',
          borderTop: '14px solid rgb(var(--sg-content))',
          filter: 'drop-shadow(0 0 6px rgb(var(--sg-content) / 0.6))',
        }}
      />

      <div
        className={cn(
          'relative h-full w-full rounded-full border-4 border-edge-strong shadow-panel-lg',
          justSettled && 'sg-reel-settle',
        )}
        style={{
          background: `conic-gradient(${gradient})`,
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? 'transform 3.2s cubic-bezier(0.11, 0.68, 0.18, 1)' : undefined,
        }}
      >
        {/* Dividers — one radial line per sector boundary, rotates with the wheel. */}
        {sectors.map((_, i) => (
          <div
            key={`divider-${i}`}
            className="absolute inset-0"
            style={{ transform: `rotate(${i * anglePerSector}deg)` }}
            aria-hidden
          >
            <div className="mx-auto h-1/2 w-px bg-void/40" />
          </div>
        ))}

        {/* Sector numbers — rotate with the wheel, same as a real roulette rim. */}
        {sectors.map((sector, i) => {
          const angle = i * anglePerSector + anglePerSector / 2;
          return (
            <div
              key={`label-${sector.id}`}
              className="absolute inset-0 flex flex-col items-center"
              style={{ transform: `rotate(${angle}deg)` }}
              aria-hidden
            >
              <span
                data-sg-numeric
                className="mt-2.5 text-xs font-bold text-void"
                style={{ textShadow: '0 1px 1px rgb(255 255 255 / 0.4)' }}
              >
                {sector.id}
              </span>
            </div>
          );
        })}
      </div>

      {/* Glossy highlight — fixed, does not rotate; reads as ambient light on glass. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 32% 24%, rgb(255 255 255 / 0.14), transparent 55%)',
        }}
      />

      {/* Hub — covers the wedge tips at centre, matches the console bezel. */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 h-[22%] w-[22%] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-edge-strong bg-surface-1 shadow-inset-edge"
      />
    </div>
  );
}
